import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import Skeleton from 'app/shared/components/common/Skeleton';
import _ from 'lodash';

import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { jsonStringify } from 'utils/json';

import { resetStateAction } from 'store/global/actions';
import { getDataBySelectorsArray } from 'store/global/selectors';

const defaultSetIsLoading = _.noop;

/* eslint-disable react/prop-types */
const withPreparedData =
  ({
    fetchData = [],
    dataSelectors = {},
    skeletonComponent = <Skeleton />,
    isShowSkeletonWhileReFetching = false,
  }) =>
  WrappedComponent =>
  ({ setIsLoading = defaultSetIsLoading, ...props }) => {
    const dispatch = useDispatch();
    const companyId = useCompanyId();
    const urlParams = useParams();
    const loanId = useLoanId();

    const params = { ...urlParams, loanId };

    const [isDataLoaded, setIsDataLoaded] = useState(!fetchData.length || false);

    const selectedData = useSelector(getDataBySelectorsArray(dataSelectors));

    // used in useEffect below for avoiding additional requests if params not changed
    const paramsInDeps = jsonStringify(params);

    // action is common way to fetch data, so companyId and urlParams are passed there to provide necessary params for fetching - they will be filtered at step of $generatedActions if they are not used there
    useEffectSafeStrictMode(() => {
      if (!fetchData.length) {
        return;
      }

      setIsLoading(true);

      if (isShowSkeletonWhileReFetching) {
        setIsDataLoaded(false);
      }

      Promise.all(fetchData.map(action => dispatch(action({ companyId, ...params }))))
        .catch(Error)
        .finally(() => {
          setIsDataLoaded(true);
          setIsLoading(false);
        });
    }, [companyId, paramsInDeps, dispatch, setIsLoading, setIsDataLoaded]);

    useEffect(() => {
      if (props.isDisableResetStateAction) {
        return;
      }

      fetchData.length && dispatch(resetStateAction());
    }, [dispatch, props.isDisableResetStateAction]);

    if (!isDataLoaded) {
      return skeletonComponent;
    }

    return <WrappedComponent {...props} {...selectedData} setIsLoading={setIsLoading} />;
  };

export default withPreparedData;
